import * as THREE from 'three';
import { cloth, emis, leather, metal, std } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { figure } from './heroes.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * The burnt library (docs/sendas/mago.md, chapter 1): its people and its loose pages, painted miniatures.
 *
 * - Isra: fifty-odd, the skipper of the Terca, broad in the shoulder, an oilskin coat the slate-green of the sea at
 *   night over a fisherman's jersey, sleeves rolled, rope-burnt hands, a knit cap, grey hair cropped, a gutting knife at
 *   the belt and a coil of line on the shoulder.
 * - The man on the pier (Ser Hadric): fifty-four, tall and spare, white hair to the collar, a long grey cape with the
 *   Order's open eye stitched small at its clasp, a burn scar down the side of his neck (a pale, puckered band).
 * - A loose page (the soot crow's place in the fight, its bones and its clips): a sheaf of black pages folded like a
 *   bird, two fans of charred leaves for wings, their edges a smouldering ember line, a thread of red ink down its spine.
 */

const isra = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 901);
  const oil = std('#2f4a48', { roughness: 0.45, metalness: 0.08 });
  const jersey = cloth('#3e4450');
  const skin = std('#c99a80', { roughness: 0.85 });
  const f = figure({
    legs: cloth('#2b2f36'),
    boots: leather('#1f1a17'),
    torso: jersey,
    shoulders: oil,
    arms: jersey,
    forearms: skin,
    hands: skin,
    skin,
  });
  c.g.add(f.fig);
  f.fig.scale.set(1.0, 0.98, 1.02);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The oilskin coat to the knee, open at the front.
  const coat = M(new THREE.CylinderGeometry(0.22, 0.31, 0.78, 12, 1, true, 0.45, Math.PI * 2 - 0.9), std('#2a4341', { roughness: 0.45, side: THREE.DoubleSide }), b);
  coat.position.y = 0.95;
  coat.rotation.y = Math.PI;
  M(new THREE.CylinderGeometry(0.2, 0.23, 0.36, 12, 1, true), oil, b).position.y = 1.26;
  // A coil of line over the right shoulder, pale hemp.
  const rope = ring(b, 0.15, 0.025, std('#a99c86', { roughness: 1 }), 1.18, 5, 14);
  rope.position.set(0.05, 1.22, 0);
  rope.rotation.set(0.3, 0.2, 1.1);
  // The gutting knife at the belt.
  M(new THREE.BoxGeometry(0.03, 0.18, 0.02), leather('#2a1f18'), b).position.set(-0.19, 0.86, 0.1);
  M(new THREE.BoxGeometry(0.015, 0.1, 0.008), metal('#c2c8cf', { roughness: 0.3 }), b).position.set(-0.19, 0.74, 0.1);
  // Her head: a dark knit cap, grey hair cropped below it, a weathered face, a strong jaw.
  const cap = M(new THREE.SphereGeometry(0.142, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), cloth('#5a2e2a'), h);
  cap.position.set(0, 1.672, -0.01);
  ring(h, 0.135, 0.022, cloth('#4a2623'), 1.665, 5, 14);
  const grey = std('#9ea2a6', { roughness: 1 });
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.03, 0.07, 0.09), grey, h).position.set(k * 0.128, 1.62, -0.02);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.645, 0.116);
    M(new THREE.BoxGeometry(0.05, 0.012, 0.015), grey, h).position.set(k * 0.044, 1.675, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.02, 0.05, 5), std('#bd8c73', { roughness: 0.85 }), h);
  nose.position.set(0, 1.62, 0.13);
  nose.rotation.x = Math.PI / 2 + 0.2;
  f.rig.armL.rotation.x = -0.15;
  return done(c, 1.74, () => undefined, f.rig);
};

const hadric = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 902);
  const grey = cloth('#5b6068');
  const skin = std('#d3b09c', { roughness: 0.85 });
  const f = figure({
    legs: cloth('#2a2d33'),
    boots: leather('#1b1816'),
    torso: cloth('#3a3f48'),
    shoulders: grey,
    arms: cloth('#3a3f48'),
    hands: skin,
    skin,
  });
  c.g.add(f.fig);
  f.fig.scale.set(0.96, 1.06, 0.96);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The long grey cape from the shoulders to the ankles, its clasp an open eye in dark iron.
  const cape = M(new THREE.CylinderGeometry(0.24, 0.36, 1.28, 14, 1, true, 0.8, Math.PI * 2 - 1.6), std('#555a62', { roughness: 0.95, side: THREE.DoubleSide }), b);
  cape.position.set(0, 0.82, -0.02);
  cape.rotation.y = Math.PI;
  const clasp = M(new THREE.TorusGeometry(0.035, 0.01, 4, 10), metal('#3a3e44', { roughness: 0.4 }), b);
  clasp.position.set(0, 1.42, 0.2);
  M(new THREE.SphereGeometry(0.014, 6, 4), std('#e8e6e2', { roughness: 0.4 }), b).position.set(0, 1.42, 0.205);
  // White hair to the collar, swept back; a lined face; the burn down the left of his neck.
  const white = std('#e4e2de', { roughness: 1 });
  const top = M(new THREE.SphereGeometry(0.138, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.6), white, h);
  top.position.set(0, 1.655, -0.02);
  top.scale.set(1.04, 1, 1.1);
  M(new THREE.BoxGeometry(0.22, 0.12, 0.06), white, h).position.set(0, 1.56, -0.12);
  const scar = M(new THREE.BoxGeometry(0.05, 0.12, 0.06), std('#c98b80', { roughness: 0.6 }), b);
  scar.position.set(-0.075, 1.5, 0.05);
  scar.rotation.z = 0.2;
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#1a1c22'), h).position.set(k * 0.042, 1.645, 0.116);
    M(new THREE.BoxGeometry(0.05, 0.012, 0.015), white, h).position.set(k * 0.044, 1.676, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.017, 0.055, 5), std('#c7a08c', { roughness: 0.85 }), h);
  nose.position.set(0, 1.62, 0.13);
  nose.rotation.x = Math.PI / 2 + 0.2;
  return done(c, 1.84, () => undefined, f.rig);
};

/** Height of a page over the ground in flight (as the crow's: its clips drop it from here). */
const PAGE_HOVER = 0.72;

/**
 * A loose page: the crow's bones (body, head, jaw, the two wings' arm and hand) under a sheaf of black pages, so the
 * crow's clips beat its leaves. The leaves' edges smoulder (an ember line), its spine has a thread of glowing ink.
 */
const page = (o?: BaseOpts): Model => {
  const c = begin(o, 0.5, 903);
  const { g, R } = c;
  g.position.y = PAGE_HOVER;
  const black = std('#16141a', { roughness: 0.85, side: THREE.DoubleSide });
  const char = std('#2a2622', { roughness: 0.95, side: THREE.DoubleSide });
  const ember = emis('#3a1a10', '#ff4f2a', 1.6, {
    roughness: 0.8,
    side: THREE.DoubleSide,
  });
  const ink = emis('#200808', '#ff3a2a', 1.2);
  const body = joint(g, V(0, 0.32, 0), 'body');
  const head = joint(body.abs, V(0, 0.4, 0.24), 'head');
  const jaw = joint(head.abs, V(0, 0.415, 0.4), 'jaw');
  // The spine: a folded quire, its stitched back up; a thread of ink along it.
  const spine = M(new THREE.BoxGeometry(0.08, 0.06, 0.52), black, body.abs);
  spine.position.set(0, 0.36, 0.02);
  M(new THREE.BoxGeometry(0.012, 0.012, 0.48), ink, body.abs).position.set(0, 0.395, 0.02);
  // Its "head": the corner of a leaf curled up, the burnt tip glowing.
  const tip = M(new THREE.ConeGeometry(0.05, 0.16, 4), char, head.abs);
  tip.rotation.x = Math.PI / 2;
  tip.position.set(0, 0.42, 0.34);
  M(new THREE.ConeGeometry(0.02, 0.05, 4), ember, jaw.abs).position.set(0, 0.42, 0.44);
  glowSprite(head.abs, '#ff4a14', 0.14, V(0, 0.43, 0.42), 0.9);
  const skel: Record<string, THREE.Object3D> = {
    body: body.p,
    head: head.p,
    jaw: jaw.p,
  };
  const leaf = new THREE.PlaneGeometry(1, 1);
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const arm = joint(body.abs, V(k * 0.1, 0.385, 0.1), `arm${s}`);
    const hand = joint(arm.abs, V(k * 0.5, 0.39, 0.11), `hand${s}`);
    skel[`arm${s}`] = arm.p;
    skel[`hand${s}`] = hand.p;
    // Three leaves fanned on the arm, three on the hand; each with its smouldering edge.
    for (let i = 0; i < 3; i++) {
      for (const [bone, x0] of [
        [arm.abs, k * 0.28],
        [hand.abs, k * 0.7],
      ] as const) {
        const z = 0.12 - i * 0.12;
        const m = M(leaf, i === 1 ? char : black, bone);
        m.position.set(x0, 0.39 + i * 0.006, z);
        m.rotation.set(-Math.PI / 2, 0, k * (0.1 + i * 0.12) + R() * 0.1);
        m.scale.set(0.38, 0.22, 1);
        const edge = M(new THREE.BoxGeometry(0.36, 0.008, 0.016), ember, bone);
        edge.position.set(x0, 0.393 + i * 0.006, z - 0.105);
        edge.rotation.y = -k * (0.1 + i * 0.12);
      }
    }
  }
  // Two leaves trailing as its tail.
  for (const k of [-1, 1]) {
    const m = M(leaf, black, body.abs);
    m.position.set(k * 0.05, 0.37, -0.32);
    m.rotation.set(-Math.PI / 2 + 0.2, 0, k * 0.3);
    m.scale.set(0.16, 0.28, 1);
  }
  return done(c, PAGE_HOVER + 0.5, () => undefined, undefined, bonesOf(c, skel));
};

/** The burnt clasp (the chapter 1 piece of the mage's look): hung from the staff's head by a chain, bronze gone black. */
export const burntClasp = (parent: THREE.Object3D): THREE.Group => {
  const g = G(parent);
  const bronze = metal('#4a3e34', { roughness: 0.55, emissive: '#1a0d08' });
  const char = std('#1c1a1a', { roughness: 0.8 });
  for (let i = 0; i < 3; i++) M(new THREE.TorusGeometry(0.012, 0.004, 3, 6), bronze, g).position.set(0, -0.02 - i * 0.022, 0);
  const plate = M(new THREE.BoxGeometry(0.07, 0.05, 0.012), bronze, g);
  plate.position.set(0, -0.1, 0);
  M(new THREE.BoxGeometry(0.03, 0.06, 0.014), char, g).position.set(0.02, -0.1, 0.002);
  limb(g, V(-0.03, -0.12, 0.008), V(0.025, -0.08, 0.008), 0.003, 0.003, emis('#300a06', '#ff4f2a', 0.6), 4);
  return g;
};

/** The library's own models by name (null: not one of them). */
export const biblioModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'isra':
      return isra(o);
    case 'hadric':
      return hadric(o);
    case 'page':
      return page(o);
    default:
      return null;
  }
};
